/**
 * Gate 1 — Upload test cases (Add test case dropdown).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Alert,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { ReactComponent as EditPencilIcon } from "../../asserts/logo/edit_pencil.svg";
import { ReactComponent as DeleteIcon } from "../../asserts/logo/delete.svg";
import { ReactComponent as TestStrategyIcon } from "../../asserts/logo/TestStrategyIcon.svg";
import { ReactComponent as TestCasesIcon } from "../../asserts/logo/TestCasesIcon.svg";
import { ReactComponent as TestSuiteIcon } from "../../asserts/logo/TestSuiteIcon.svg";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import { GATE_SUBTABS_PL, GATE_SUBTABS_PR, GATE_SUBTABS_GAP } from "../../components/layout/layoutConstants";
import SelectDropdownTheme from "../../components/SelectDropdownTheme";
import SelectDropdownIcon from "../../components/SelectDropdownIcon";
import queueEmptySrc from "../../asserts/logo/QueueEmptyDoc.png";
import browseIconSrc from "../../asserts/logo/BrowseIcon.png";
import { ReactComponent as AlertTriangleOrange } from "../../asserts/logo/AlertTriangleOrange.svg";
import { ReactComponent as GreenCheckCircle } from "../../asserts/logo/GreenCheckCircle.svg";
import dangerIcon from "../../asserts/logo/Danger.svg";
import { TRUST_ATTRIBUTES, trustAttributeLabel } from "../../config/trustAttributes";

function FileDocIcon({ error = false, size = 36 }) {
  const color = error ? "#F04438" : "#005BF0";
  const bg = error ? "#FFF1F0" : "#E8F1FF";
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden>
      <rect width="32" height="32" rx="8" fill={bg} />
      <g transform="translate(4 4)">
        <path
          d="M14 2.5H6.5C5.12 2.5 4 3.62 4 5v14c0 1.38 1.12 2.5 2.5 2.5h11c1.38 0 2.5-1.12 2.5-2.5V8.5L14 2.5Z"
          stroke={color}
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path d="M14 2.5V8.5H20" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 12.2h4" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8 15.4h8" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
        <path d="M8 18.6h8" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      </g>
    </svg>
  );
}

function buildExtractionSummary(files) {
  const templates = [
    { extracted: 45, ready: 20, needs: 25, duplicates: 5 },
    { extracted: 25, ready: 10, needs: 15, duplicates: 5 },
  ];
  const rows = files.map((item, i) => ({
    name: item.file.name,
    ...templates[i % templates.length],
  }));
  return {
    rows,
    total: 70,
    ready: 20,
    needs: 40,
    duplicates: 10,
    extractedTotal: 70,
  };
}

function SuccessCheckMark({ size = 48 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: "#E7F8EF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg width={size * 0.42} height={size * 0.42} viewBox="0 0 16 16" fill="none" aria-hidden>
        <path d="M2.5 8.2l3.6 3.6 7.4-8" stroke="#12B76A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Box>
  );
}

function StatCard({ value, label, hint, bgcolor, color }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, bgcolor, borderRadius: "8px", px: 1.5, py: 1.5, textAlign: "center" }}>
      <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 800, fontSize: 28, lineHeight: "32px", color }}>
        {value}
      </Typography>
      <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 700, fontSize: 12, color: "#1A1A1A", mt: 0.5, lineHeight: "16px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 11, color: "#707070", mt: 0.25, lineHeight: "14px" }}>
        {hint}
      </Typography>
    </Box>
  );
}

function QueueEmptyIllustration() {
  return (
    <Box
      component="img"
      src={queueEmptySrc}
      alt=""
      sx={{ width: 116, height: 130, display: "block", objectFit: "contain" }}
    />
  );
}

const READY_SEED = [
  { name: "User Login Authentication", trust: "Security", risk: "Prompt Injection", metric: "Accuracy" },
  { name: "Password Reset Flow", trust: "Security", risk: "Hallucination", metric: "Precision" },
  { name: "Multi-Factor Auth Verification", trust: "Privacy", risk: "Prompt Injection", metric: "Accuracy" },
  { name: "Session Timeout Handling", trust: "Reliability", risk: "Hallucination", metric: "Recall" },
  { name: "API Rate Limit Testing", trust: "Reliability", risk: "PII Leakage", metric: "Accuracy" },
];

const NEEDS_SEED = [
  { name: "User Login Authentication", trust: "Security", risk: "Prompt Injection", metric: "Accuracy" },
  { name: "Password Reset Flow", trust: "Security", risk: "–", metric: "–" },
  { name: "Multi-Factor Auth Verification", trust: "–", risk: "Prompt Injection", metric: "Accuracy" },
  { name: "Session Timeout Handling", trust: "–", risk: "Hallucination", metric: "–" },
  { name: "API Rate Limit Testing", trust: "Reliability", risk: "–", metric: "Accuracy" },
];

function buildQueueCases() {
  const readyN = 30;
  const needsN = 40;
  const dupsN = 10;
  const extraTrust = ["Security", "Reliability", "Fairness & Policy"];
  const extraRisk = ["Prompt Injection", "Hallucination", "PII Leakage"];
  const extraMetric = ["Faithfulness score", "Accuracy", "Precision"];
  const make = (status, count, seed) =>
    Array.from({ length: count }, (_, i) => ({
      id: `${status}-${i}`,
      status,
      name: seed[i]?.name || `${status === "ready" ? "Imported" : status === "needs" ? "Incomplete" : "Duplicate"} Test Case ${i + 1}`,
      trust: seed[i]?.trust || extraTrust[i % extraTrust.length],
      risk: seed[i]?.risk || extraRisk[i % extraRisk.length],
      metric: seed[i]?.metric || extraMetric[i % extraMetric.length],
    }));
  return [...make("ready", readyN, READY_SEED), ...make("needs", needsN, NEEDS_SEED), ...make("duplicates", dupsN, [])];
}

// Display labels for the seven Gate 1 trust attributes.
const TRUST_OPTIONS = TRUST_ATTRIBUTES.map(trustAttributeLabel);
const RISK_OPTIONS = ["Prompt Injection", "Hallucination", "PII Leakage"];
const METRIC_OPTIONS = ["Accuracy", "Precision", "Recall", "Faithfulness score"];

function FieldLabel({ children, required }) {
  return (
    <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, fontWeight: 700, color: "#344054", mb: 0.75 }}>
      {required && (
        <Typography component="span" sx={{ color: "#DE350B", mr: 0.25 }}>
          *
        </Typography>
      )}
      {children}
    </Typography>
  );
}

function ViewTestCaseDrawer({ open, testCase, onClose, onSave }) {
  const [form, setForm] = useState({ name: "", trust: "", risk: "", metric: "", description: "" });
  useEffect(() => {
    if (!testCase) return;
    const clean = (v) => (!v || v === "–" ? "" : v);
    setForm({
      name: testCase.name || "",
      trust: clean(testCase.trust),
      risk: clean(testCase.risk),
      metric: clean(testCase.metric),
      description: testCase.description || "",
    });
  }, [testCase]);
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const canSave = Boolean(form.name.trim() && form.trust && form.risk && form.metric);
  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 14, fontFamily: "Lato, sans-serif" },
  };
  const selectSx = { borderRadius: "4px", fontSize: 14, "& .MuiSelect-select": { color: "inherit" } };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 420, md: 440 }, maxWidth: "100vw", boxShadow: "-8px 0 32px rgba(16,24,40,0.14)" },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(52,64,84,0.45)" } } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 16, fontWeight: 700, color: "#1A1A1A" }}>
            Edit Test Case
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: "#555" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", px: 3, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <FieldLabel>Test Case Title</FieldLabel>
            <TextField fullWidth size="small" value={form.name} onChange={set("name")} sx={fieldSx} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <FieldLabel required>Trust Attribute</FieldLabel>
              <FormControl fullWidth size="small">
                <Select value={form.trust} onChange={set("trust")} displayEmpty sx={selectSx} IconComponent={SelectDropdownIcon}>
                  <MenuItem value="" disabled>
                    Select attribute
                  </MenuItem>
                  {TRUST_OPTIONS.map((a) => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FieldLabel required>Risk</FieldLabel>
              <FormControl fullWidth size="small">
                <Select
                  value={form.risk}
                  onChange={set("risk")}
                  displayEmpty
                  sx={selectSx}
                  IconComponent={SelectDropdownIcon}
                  renderValue={(v) => v || <Box component="span" sx={{ color: "#98A2B3" }}>Select risk type</Box>}
                >
                  <MenuItem value="" disabled>
                    Select risk type
                  </MenuItem>
                  {RISK_OPTIONS.map((a) => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box>
            <FieldLabel required>Metric</FieldLabel>
            <FormControl fullWidth size="small">
              <Select
                value={form.metric}
                onChange={set("metric")}
                displayEmpty
                sx={selectSx}
                IconComponent={SelectDropdownIcon}
                renderValue={(v) => v || <Box component="span" sx={{ color: "#98A2B3" }}>Select metric</Box>}
              >
                <MenuItem value="" disabled>
                  Select metric
                </MenuItem>
                {METRIC_OPTIONS.map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FieldLabel>Description</FieldLabel>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              placeholder="Add a description for this test case..."
              value={form.description}
              onChange={set("description")}
              sx={fieldSx}
            />
          </Box>
        </Box>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            borderTop: "1px solid #EAECF0",
          }}
        >
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 12, color: "#98A2B3" }}>
            Saved to Custom Test Strategies.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button disableElevation disableRipple onClick={onClose} sx={footerGhostBtnSx}>
              Cancel
            </Button>
            <Button
              disableElevation
              disableRipple
              disabled={!canSave}
              onClick={() => onSave({ ...testCase, ...form })}
              sx={{ ...footerPrimaryBtnSx, px: "16px" }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

function DismissIncompleteDialog({ open, count, onClose, onConfirm }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          width: "100%",
          maxWidth: 480,
          mx: 2,
          px: { xs: 3, sm: "48px" },
          pt: "36px",
          pb: "32px",
          position: "relative",
          boxShadow:
            "0px 0px 10px 0px rgba(33, 34, 38, 0.12), 0px 10px 10px 0px rgba(0, 0, 0, 0.24)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(16, 24, 40, 0.45)" },
        },
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        sx={{ position: "absolute", top: 8, right: 8, color: "#98A2B3" }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <Box
          component="img"
          src={dangerIcon}
          alt=""
          sx={{ width: 48, height: 44, display: "block" }}
        />
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 800,
            fontSize: 18,
            lineHeight: "24px",
            color: "#1A1A1A",
            mt: 1.75,
          }}
        >
          Dismiss {count} incomplete test cases?
        </Typography>
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 400,
            fontSize: 13,
            lineHeight: "20px",
            color: "#667085",
            mt: 1,
            maxWidth: 360,
          }}
        >
          These test cases have missing fields and won&apos;t be imported. This action cannot be undone.
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: "8px", pt: 2.5, flexWrap: "wrap" }}>
          <Button
            disableElevation
            disableRipple
            onClick={onClose}
            sx={{
              ...footerBtnTextSx,
              minWidth: 0,
              height: 40,
              minHeight: 40,
              px: "16px",
              borderRadius: "4px",
              color: "#555555",
              border: "1px solid #BBBBBB",
              bgcolor: "#F5F5F5",
              backgroundImage: "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%)",
              boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
              "&:hover": { bgcolor: "#EEEEEE", border: "1px solid #BBBBBB" },
            }}
          >
            Cancel
          </Button>
          <Button
            disableElevation
            disableRipple
            onClick={onConfirm}
            sx={{
              ...footerBtnTextSx,
              minWidth: 0,
              height: 40,
              minHeight: 40,
              px: "16px",
              borderRadius: "4px",
              color: "#FFFFFF",
              bgcolor: "#DE350B",
              backgroundImage:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
              boxShadow: "0px 1px 2px 0px rgba(33, 34, 38, 0.2)",
              "&:hover": { bgcolor: "#BF2600" },
            }}
          >
            Dismiss {count} Test Cases
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

function DeleteUploadedDocumentDialog({ open, fileName, onClose, onConfirm }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          width: "100%",
          maxWidth: 480,
          mx: 2,
          px: { xs: 3, sm: "48px" },
          pt: "36px",
          pb: "32px",
          position: "relative",
          boxShadow:
            "0px 0px 10px 0px rgba(33, 34, 38, 0.12), 0px 10px 10px 0px rgba(0, 0, 0, 0.24)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(16, 24, 40, 0.45)" },
        },
      }}
    >
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        sx={{ position: "absolute", top: 8, right: 8, color: "#98A2B3" }}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <Box component="img" src={dangerIcon} alt="" sx={{ width: 48, height: 44, display: "block" }} />
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 800,
            fontSize: 18,
            lineHeight: "24px",
            color: "#1A1A1A",
            mt: 1.75,
            wordBreak: "break-word",
          }}
        >
          Delete {fileName || "this document"}?
        </Typography>
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 400,
            fontSize: 13,
            lineHeight: "20px",
            color: "#667085",
            mt: 1,
            maxWidth: 360,
          }}
        >
          This document will be removed from your uploaded files. This action cannot be undone.
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: "8px", pt: 2.5, flexWrap: "wrap" }}>
          <Button
            disableElevation
            disableRipple
            onClick={onClose}
            sx={{
              ...footerBtnTextSx,
              minWidth: 0,
              height: 40,
              minHeight: 40,
              px: "16px",
              borderRadius: "4px",
              color: "#555555",
              border: "1px solid #BBBBBB",
              bgcolor: "#F5F5F5",
              backgroundImage: "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%)",
              boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
              "&:hover": { bgcolor: "#EEEEEE", border: "1px solid #BBBBBB" },
            }}
          >
            Cancel
          </Button>
          <Button
            disableElevation
            disableRipple
            onClick={onConfirm}
            sx={{
              ...footerBtnTextSx,
              minWidth: 0,
              height: 40,
              minHeight: 40,
              px: "16px",
              borderRadius: "4px",
              color: "#FFFFFF",
              bgcolor: "#DE350B",
              backgroundImage:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
              boxShadow: "0px 1px 2px 0px rgba(33, 34, 38, 0.2)",
              "&:hover": { bgcolor: "#BF2600" },
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

function QueueReviewPanel({ cases, onDelete, onEdit, onDismissIncomplete, onImportReady, onImportSelected, importBlocked, onDismissImportBlocked }) {
  const [tab, setTab] = useState("ready");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState(() => new Set());
  const [dismissedCount, setDismissedCount] = useState(0);
  const [confirmDismissOpen, setConfirmDismissOpen] = useState(false);
  const [attentionResolved, setAttentionResolved] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [importingCount, setImportingCount] = useState(0);
  const importTimer = useRef(null);
  const counts = {
    ready: cases.filter((c) => c.status === "ready").length,
    needs: cases.filter((c) => c.status === "needs").length,
    duplicates: cases.filter((c) => c.status === "duplicates").length,
  };
  const filtered = cases.filter((c) => c.status === tab);
  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage);
  const allPageSelected = pageRows.length > 0 && pageRows.every((r) => selected.has(r.id));
  const somePageSelected = pageRows.some((r) => selected.has(r.id)) && !allPageSelected;
  const showNeedsResolved = tab === "needs" && counts.needs === 0 && attentionResolved;

  const beginImport = (n, run) => {
    if (!n) return;
    setImportedCount(0);
    setDismissedCount(0);
    setImportingCount(n);
    onDismissImportBlocked?.();
    if (importTimer.current) clearTimeout(importTimer.current);
    importTimer.current = setTimeout(() => {
      run?.();
      setImportingCount(0);
      setImportedCount(n);
      setTab("ready");
    }, 1600);
  };

  const handleImportReady = () => {
    if (counts.needs === 0) {
      setAttentionResolved(true);
      setTab("needs");
      setDismissedCount(0);
      setSelected(new Set());
      onDismissImportBlocked?.();
      return;
    }
    const n = selected.size > 0 ? selected.size : counts.ready;
    beginImport(n, () => onImportReady());
  };

  useEffect(() => () => {
    if (importTimer.current) clearTimeout(importTimer.current);
  }, []);

  useEffect(() => {
    if (importBlocked) setTab("needs");
  }, [importBlocked]);

  const tabs = [
    { id: "ready", label: `Ready to Import (${counts.ready})` },
    { id: "needs", label: `Needs Attention (${counts.needs})` },
    { id: "duplicates", label: `Duplicates (${counts.duplicates})` },
  ];
  const headerCell = {
    fontFamily: "Lato, sans-serif",
    fontSize: 12,
    fontWeight: 600,
    color: "#98A2B3",
    py: 1.25,
    bgcolor: "#FFFFFF",
  };
  const bodyCell = {
    py: 1.25,
    fontFamily: "Lato, sans-serif",
    fontSize: 13,
  };

  return (
    <Box sx={{ flex: 1, height: "100%", display: "flex", flexDirection: "column", minHeight: 0, pt: 2, pb: 2, boxSizing: "border-box" }}>
      <Box sx={{ flexShrink: 0, mb: 0.5, px: 2.5 }}>
        <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 800, fontSize: 16, color: "#1A1A1A", mb: 1.25 }}>
          {cases.length} test cases in queue
        </Typography>
        <Box sx={{ display: "flex", gap: 2.5, borderBottom: "1px solid #E6E6E6" }}>
          {tabs.map((t) => {
            const active = tab === t.id;
            return (
              <Box
                key={t.id}
                component="button"
                type="button"
                onClick={() => {
                  setTab(t.id);
                  setPage(1);
                  setSelected(new Set());
                  setDismissedCount(0);
                  setImportedCount(0);
                  setImportingCount(0);
                }}
                sx={{
                  border: 0,
                  bgcolor: "transparent",
                  p: 0,
                  pb: 1,
                  mb: "-1px",
                  cursor: "pointer",
                  fontFamily: "Lato, sans-serif",
                  fontSize: 13,
                  fontWeight: active ? 700 : 400,
                  color: active ? "#1A1A1A" : "#707070",
                  borderBottom: active ? "2px solid #005BF0" : "2px solid transparent",
                }}
              >
                {t.label}
              </Box>
            );
          })}
        </Box>
      </Box>
      {importBlocked && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#FEF3F2",
            borderRadius: "6px",
            mx: 2.5,
            px: 1.5,
            py: 1,
            mb: 1.25,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            <ErrorCircleIcon size={18} />
          </Box>
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#B42318", fontWeight: 600, flex: 1 }}>
            Cannot import incomplete test cases. Complete missing fields or move to Ready to Import.
          </Typography>
          <IconButton
            size="small"
            aria-label="Dismiss import warning"
            onClick={onDismissImportBlocked}
            sx={{ p: "2px", color: "#B42318", ml: 0.5 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}
      {importingCount > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#EAF3FF",
            borderRadius: "6px",
            mx: 2.5,
            px: 1.5,
            py: 1,
            mb: 1.25,
            flexShrink: 0,
          }}
        >
          <CircularProgress size={16} thickness={5} sx={{ color: "#005BF0", flexShrink: 0 }} />
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#005BF0", fontWeight: 600 }}>
            Importing {importingCount} test cases... Please wait.
          </Typography>
        </Box>
      )}
      {importedCount > 0 && !importBlocked && importingCount === 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#ECFDF3",
            borderRadius: "6px",
            mx: 2.5,
            px: 1.5,
            py: 1,
            mb: 1.25,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", flexShrink: 0, alignItems: "center" }}>
            <GreenCheckCircle style={{ width: 20, height: 20, display: "block" }} />
          </Box>
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#027A48", fontWeight: 600, flex: 1 }}>
            {String(importedCount).padStart(2, "0")} test cases imported successfully.
          </Typography>
          <IconButton
            size="small"
            aria-label="Dismiss import success"
            onClick={() => setImportedCount(0)}
            sx={{ p: "2px", color: "#027A48" }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}
      {dismissedCount > 0 && !importBlocked && selected.size === 0 && importedCount === 0 && importingCount === 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#ECFDF3",
            borderRadius: "6px",
            mx: 2.5,
            px: 1.5,
            py: 1,
            mb: 1.25,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            <SuccessCircleIcon size={18} />
          </Box>
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#027A48", fontWeight: 600 }}>
            {dismissedCount} test case{dismissedCount === 1 ? "" : "s"} dismissed successfully.
          </Typography>
        </Box>
      )}
      {tab === "needs" && counts.needs > 0 && selected.size === 0 && !importBlocked && dismissedCount === 0 && importedCount === 0 && importingCount === 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "#FFF8E8",
            borderRadius: "6px",
            mx: 2.5,
            px: 1.5,
            py: 1,
            mb: 1.25,
            flexShrink: 0,
          }}
        >
          <WarningTriangle size={18} />
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#B54708", fontWeight: 600 }}>
            These test cases have missing information. Complete required fields, or Dismiss to skip them.
          </Typography>
        </Box>
      )}
      {showNeedsResolved ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 3,
            minHeight: 280,
          }}
        >
          <SuccessCheckMark size={36} />
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 800, fontSize: 16, color: "#1A1A1A", mt: 1.5 }}>
            All resolved
          </Typography>
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#667085", mt: 0.75, maxWidth: 360 }}>
            {counts.duplicates > 0
              ? "All attention items resolved. Resolve duplicates to proceed."
              : "All attention items resolved."}
          </Typography>
        </Box>
      ) : (
        <>
      {selected.size > 0 && importedCount === 0 && importingCount === 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            px: "20px",
            py: 0.75,
            borderBottom: "1px solid #E6E6E6",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Checkbox
              size="small"
              checked
              onChange={() => setSelected(new Set())}
              sx={{ p: 0.5, color: "#005BF0", "&.Mui-checked": { color: "#005BF0" } }}
            />
            <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, fontWeight: 700, color: "#005BF0" }}>
              {selected.size} selected
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => {
              const ids = [...selected];
              onDelete(ids);
              setSelected(new Set());
              setDismissedCount(ids.length);
              onDismissImportBlocked?.();
            }}
            sx={{
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              fontFamily: "Lato, sans-serif",
              fontSize: 13,
              fontWeight: 700,
              color: "#F04438",
              p: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <DeleteIcon style={{ width: 14, height: 16, display: "block" }} />
            Dismiss Selected
          </Box>
        </Box>
      )}
      <TableContainer
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-track": { bgcolor: "#F2F4F7" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#C4C4C4", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb:hover": { bgcolor: "#98A2B3" },
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            width: "100%",
            minWidth: "100%",
            tableLayout: "auto",
            borderCollapse: "collapse",
            "& .MuiTableCell-root": {
              borderBottom: "1px solid #E6E6E6 !important",
            },
            "& .MuiTableRow-root:hover": { bgcolor: "#F7F7F7" },
            "& .MuiTableCell-root:first-of-type": { pl: "20px" },
            "& .MuiTableCell-root:last-of-type": { pr: "20px" },
            "& .MuiTableHead .MuiTableCell-root": {
              bgcolor: "#FFFFFF",
              top: 0,
              zIndex: 2,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={headerCell}>
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  onChange={() => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                  if (allPageSelected) pageRows.forEach((r) => next.delete(r.id));
                  else pageRows.forEach((r) => next.add(r.id));
                      return next;
                    });
                  }}
                  sx={{ color: "#D0D5DD", "&.Mui-checked": { color: "#005BF0" } }}
                />
              </TableCell>
              {["Test Case Name", "Trust Attribute", "Risk", "Metric", "Actions"].map((h) => (
                <TableCell key={h} align={h === "Actions" ? "right" : "left"} sx={headerCell}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row) => {
              const isOn = selected.has(row.id);
              const dash = (v) => (!v || v === "–" ? "–" : v);
              return (
              <TableRow key={row.id} hover selected={isOn} sx={{ bgcolor: isOn ? "#F5F9FF" : undefined }}>
                <TableCell padding="checkbox" sx={bodyCell}>
                  <Checkbox
                    size="small"
                    checked={isOn}
                    onChange={() => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(row.id)) next.delete(row.id);
                        else next.add(row.id);
                        return next;
                      });
                    }}
                    sx={{ color: "#D0D5DD", "&.Mui-checked": { color: "#005BF0" } }}
                  />
                </TableCell>
                <TableCell sx={bodyCell}>
                  <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, fontWeight: 700, color: "#005BF0" }}>
                    {row.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...bodyCell, color: dash(row.trust) === "–" ? "#98A2B3" : "#344054" }}>
                  {dash(row.trust)}
                </TableCell>
                <TableCell sx={{ ...bodyCell, color: dash(row.risk) === "–" ? "#98A2B3" : "#344054" }}>
                  {dash(row.risk)}
                </TableCell>
                <TableCell sx={{ ...bodyCell, color: dash(row.metric) === "–" ? "#98A2B3" : "#344054" }}>
                  {dash(row.metric)}
                </TableCell>
                <TableCell align="right" sx={{ ...bodyCell, whiteSpace: "nowrap" }}>
                  <IconButton size="small" aria-label="Edit test case" onClick={() => onEdit(row)} sx={{ color: "#005BF0" }}>
                    <EditPencilIcon style={{ width: 16, height: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Delete test case"
                    onClick={() => {
                      onDelete(row.id);
                      setSelected((prev) => {
                        const next = new Set(prev);
                        next.delete(row.id);
                        return next;
                      });
                      setDismissedCount(1);
                      onDismissImportBlocked?.();
                    }}
                  >
                    <DeleteIcon style={{ width: 16, height: 18, display: "block" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          pt: 1.25,
          pb: 1.25,
          flexWrap: "wrap",
          gap: 1,
          flexShrink: 0,
          borderBottom: "1px solid #E6E6E6",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 12, color: "#667085" }}>Rows per page</Typography>
          <Select
            size="small"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
            sx={{
              minWidth: 52,
              height: 32,
              fontSize: 13,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D0D5DD" },
              "& .MuiSelect-select": { py: "6px", pl: "10px", pr: "28px !important" },
            }}
            IconComponent={SelectDropdownIcon}
          >
            {[10, 25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontSize: 12, color: "#667085" }}>
            {filtered.length ? (safePage - 1) * rowsPerPage + 1 : 0}-{Math.min(safePage * rowsPerPage, filtered.length)} of {filtered.length}
          </Typography>
        </Box>
        <Pagination
          count={pageCount}
          page={safePage}
          onChange={(_, p) => setPage(p)}
          size="small"
          shape="rounded"
          siblingCount={3}
          boundaryCount={1}
          sx={{
            "& .MuiPaginationItem-root.Mui-selected": { bgcolor: "#005BF0", color: "#fff", "&:hover": { bgcolor: "#004ACC" } },
          }}
        />
      </Box>
      {(importingCount === 0 && importedCount === 0 && (selected.size > 0 || (tab === "needs" && (counts.needs > 0 || !attentionResolved)))) && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", flexShrink: 0, pt: 1, px: 2.5 }}>
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#707070" }}>
            {selected.size > 0
              ? `${selected.size} selected — import or remove from queue`
              : counts.needs > 0
                ? `${counts.needs} incomplete — resolve or dismiss to proceed`
                : ""}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {counts.needs > 0 && (
            <Button
              disableElevation
              disableRipple
              disabled={selected.size > 0}
              onClick={() => setConfirmDismissOpen(true)}
              sx={{
                ...footerBtnTextSx,
                height: 36,
                minHeight: 36,
                px: "14px",
                borderRadius: "4px",
                color: "#707070",
                bgcolor: "#F2F4F7",
                border: "1px solid #E6E6E6",
                boxShadow: "none",
                "&:hover:not(.Mui-disabled)": { bgcolor: "#E8EAED" },
                "&.Mui-disabled": { color: "#C4C4C4", bgcolor: "#F5F5F5", border: "1px solid #E8E8E8" },
              }}
            >
              Dismiss All Incomplete ({counts.needs})
            </Button>
            )}
            {selected.size > 0 ? (
              <Button
                disableElevation
                disableRipple
                onClick={() => {
                  const ids = [...selected];
                  beginImport(ids.length, () => onImportSelected?.(ids));
                }}
                sx={{ ...footerPrimaryBtnSx, height: 36, minHeight: 36, px: "14px" }}
              >
                Import Selected ({selected.size})
              </Button>
            ) : (
            <Button
              disableElevation
              disableRipple
              disabled={counts.ready === 0}
              onClick={handleImportReady}
              sx={{ ...footerPrimaryBtnSx, height: 36, minHeight: 36, px: "14px" }}
            >
              Import Ready{counts.ready > 0 ? ` (${counts.ready})` : ""}
            </Button>
            )}
          </Box>
        </Box>
      )}
        </>
      )}
      <DismissIncompleteDialog
        open={confirmDismissOpen}
        count={counts.needs}
        onClose={() => setConfirmDismissOpen(false)}
        onConfirm={() => {
          const n = counts.needs;
          onDismissIncomplete();
          setSelected(new Set());
          setDismissedCount(n);
          setConfirmDismissOpen(false);
        }}
      />
    </Box>
  );
}

function ExtractionCompletePanel({ summary, onReview }) {
  const headerCell = {
    fontFamily: "Lato, sans-serif",
    fontSize: 12,
    fontWeight: 600,
    color: "#98A2B3",
    textAlign: "right",
    pb: 1,
  };
  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", px: { xs: 2, md: 3.5 }, py: 3, overflow: "auto" }}>
      <SuccessCheckMark size={52} />
      <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 800, fontSize: 20, color: "#1A1A1A", mt: 1.5 }}>
        Extraction Complete
      </Typography>
      <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#707070", mb: 3, mt: 0.75, textAlign: "center" }}>
        ValidAIte has analyzed your documents and extracted test cases.
      </Typography>

      <Box sx={{ display: "flex", gap: 1.25, width: "100%", mb: 3 }}>
        <StatCard value={summary.extractedTotal || 70} label="Total Test Cases" hint="Extracted from CSV" bgcolor="#EEF2FF" color="#4F14FF" />
        <StatCard value={summary.ready} label="Ready to Import" hint="All required fields mapped" bgcolor="#E7F8EF" color="#12B76A" />
        <StatCard value={summary.needs} label="Needs Attention" hint="Missing attributes" bgcolor="#FFF8E8" color="#F04438" />
        <StatCard value={summary.duplicates} label="Duplicates" hint="Match existing records" bgcolor="#F2F4F7" color="#667085" />
      </Box>

      <Box sx={{ width: "100%" }}>
        <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 700, fontSize: 14, color: "#1A1A1A", mb: 1.5 }}>
          Document Breakdown
        </Typography>
      </Box>
      <TableContainer
        sx={{
          width: { xs: "calc(100% + 32px)", md: "calc(100% + 56px)" },
          ml: { xs: -2, md: -3.5 },
          overflow: "visible",
          borderTop: "1px solid #E6E6E6",
          borderBottom: "1px solid #E6E6E6",
        }}
      >
        <Table
          size="small"
          sx={{
            width: "100%",
            tableLayout: "fixed",
            borderCollapse: "collapse",
            "& .MuiTableCell-root": {
              borderBottom: "1px solid #E6E6E6 !important",
              py: "10px",
              px: "8px",
              fontFamily: "Lato, sans-serif",
            },
            "& .MuiTableHead .MuiTableCell-root": {
              borderBottom: "1px solid #E6E6E6 !important",
            },
            "& .MuiTableBody .MuiTableRow-root:last-of-type .MuiTableCell-root": {
              borderBottom: "none !important",
            },
            "& .MuiTableCell-root:first-of-type": { pl: { xs: 2, md: 3.5 } },
            "& .MuiTableCell-root:last-of-type": { pr: { xs: 2, md: 3.5 } },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="left"
                sx={{
                  ...headerCell,
                  width: "42%",
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  color: "#98A2B3",
                  fontWeight: 400,
                  fontSize: 12,
                }}
              >
                Document
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...headerCell,
                  width: "13%",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  color: "#98A2B3",
                  fontWeight: 400,
                  fontSize: 12,
                }}
              >
                Extracted
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...headerCell,
                  width: "12%",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  color: "#98A2B3",
                  fontWeight: 400,
                  fontSize: 12,
                }}
              >
                Ready
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...headerCell,
                  width: "20%",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  color: "#98A2B3",
                  fontWeight: 400,
                  fontSize: 12,
                }}
              >
                Needs Attention
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...headerCell,
                  width: "13%",
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  color: "#98A2B3",
                  fontWeight: 400,
                  fontSize: 12,
                }}
              >
                Duplicates
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.rows.map((row, i) => (
              <TableRow key={`${row.name}-${i}`}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                    <Box sx={{ flexShrink: 0, display: "flex" }}>
                      <FileDocIcon size={32} />
                    </Box>
                    <Typography noWrap sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, fontWeight: 700, color: "#1A1A1A", lineHeight: "18px" }}>
                      {row.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 400, color: "#1A1A1A" }}>
                  {row.extracted}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 400, color: "#1A1A1A" }}>
                  {row.ready}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 400, color: "#1A1A1A" }}>
                  {row.needs}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 400, color: "#1A1A1A" }}>
                  {row.duplicates}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button disableElevation disableRipple onClick={onReview} sx={{ ...footerPrimaryBtnSx, mt: 3, px: "28px" }}>
        Review & Import
      </Button>
    </Box>
  );
}

const DOC_TABS = [
  { label: "Test Strategies", Icon: TestStrategyIcon },
  { label: "Test Cases", Icon: TestCasesIcon },
  { label: "Test Suite", Icon: TestSuiteIcon },
];
const ACTIVE_GATE = 1;
const TAB_ACTIVE = "#555555";
const TAB_INACTIVE = "#555555";
const TAB_UNDERLINE = "#005BF0";

const ACCEPTED_EXT = [".pdf", ".mov", ".xls", ".xlsx", ".txt", ".doc", ".docx"];
const ACCEPTED_MIME = [
  "application/pdf",
  "video/quicktime",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_BYTES = 50 * 1024 * 1024;

const FOOTER_BTN_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.04)";
const FOOTER_BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const FOOTER_PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

const footerBtnTextSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  textTransform: "none",
};

const footerGhostBtnSx = {
  ...footerBtnTextSx,
  minWidth: 0,
  height: 40,
  minHeight: 40,
  px: "16px",
  borderRadius: "4px",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  color: "#005BF0",
  boxShadow: FOOTER_BTN_SHADOW,
  "&:hover": { bgcolor: "#F0F6FF", boxShadow: FOOTER_BTN_SHADOW },
  "& .MuiTouchRipple-root": { display: "none" },
};

const footerPrimaryBtnSx = {
  ...footerBtnTextSx,
  minWidth: 0,
  height: 40,
  minHeight: 40,
  px: "16px",
  borderRadius: "4px",
  border: "1px solid transparent",
  color: "#FFFFFF",
  backgroundImage: FOOTER_PRIMARY_BG,
  boxShadow: FOOTER_BTN_SHADOW,
  "&:hover:not(.Mui-disabled)": { filter: "brightness(0.96)" },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
  },
};

const queueBtnSx = {
  ...footerBtnTextSx,
  height: 40,
  minHeight: 40,
  width: "100%",
  borderRadius: "4px",
  boxShadow: "none",
  bgcolor: "#FFFFFF",
  color: "#005BF0",
  border: "1px solid #005BF0",
  "&:hover:not(.Mui-disabled)": { bgcolor: "#F0F6FF" },
  "&.Mui-disabled": {
    color: "#C4C4C4",
    bgcolor: "#F3F3F3",
    border: "1px solid #E8E8E8",
  },
};

const addingBtnSx = {
  ...footerBtnTextSx,
  height: 40,
  minHeight: 40,
  minWidth: 112,
  width: "auto",
  px: "20px",
  borderRadius: "4px",
  boxShadow: "none",
  color: "#B0B0B0",
  bgcolor: "#F3F3F3",
  border: "1px solid #E8E8E8",
  "&.Mui-disabled": {
    color: "#B0B0B0",
    bgcolor: "#F3F3F3",
    border: "1px solid #E8E8E8",
  },
};

function fileExt(name) {
  const i = String(name || "").lastIndexOf(".");
  return i >= 0 ? String(name).slice(i).toLowerCase() : "";
}

function isAcceptedFile(file) {
  const ext = fileExt(file.name);
  if (ACCEPTED_EXT.includes(ext)) return true;
  if (file.type && ACCEPTED_MIME.includes(file.type)) return true;
  return false;
}

function formatSize(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileKey(file) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function relativeLabel(addedAt) {
  const mins = Math.floor((Date.now() - addedAt) / 60000);
  if (mins < 1) return "Just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} mins ago`;
  return "Just now";
}

function sizeLimitMessage(name) {
  return `"${name}" exceeds the 50MB file size limit. Remove the file and try again with a smaller document.`;
}

function unsupportedFormatMessage(name) {
  return `"${name}" is not a supported format. Please upload PDF, DOC, XLS, or TXT files.`;
}

function processFailMessage(failed, total) {
  return `${failed} of ${total} documents failed to process. The files may be corrupted, password-protected, or contain unsupported content.`;
}

async function inspectExtractableFile(file) {
  try {
    if (!file || !file.size) return "bad";
    const ext = fileExt(file.name);
    const read = async (start, length) => {
      const buf = await file.slice(start, start + length).arrayBuffer();
      return new TextDecoder("latin1").decode(new Uint8Array(buf));
    };
    const headBytes = new Uint8Array(await file.slice(0, Math.min(file.size, 128 * 1024)).arrayBuffer());
    const head = new TextDecoder("latin1").decode(headBytes);
    const tailLen = Math.min(file.size, 512 * 1024);
    const tail = await read(Math.max(0, file.size - tailLen), tailLen);
    const sample = head + tail;
    const isZip = headBytes.length >= 2 && headBytes[0] === 0x50 && headBytes[1] === 0x4b;
    const isOle = headBytes.length >= 4 && headBytes[0] === 0xd0 && headBytes[1] === 0xcf && headBytes[2] === 0x11 && headBytes[3] === 0xe0;
    const encrypted = /\/Encrypt\b|EncryptedPackage|EncryptionInfo|StrongEncryptionDataSpace/i.test(sample);

    const nameHint = /password|encrypt|locked|protect|corrupt/i.test(file.name || "");
    if (nameHint) return "bad";
    if (ext === ".pdf") {
      if (!head.includes("%PDF")) return "bad";
      if (encrypted || sample.includes("/Encrypt")) return "bad";
      return "ok";
    }
    if (ext === ".docx" || ext === ".xlsx") {
      if (isOle || encrypted) return "bad";
      if (!isZip) return "bad";
      return "ok";
    }
    if (ext === ".doc" || ext === ".xls") {
      if (!isOle && !isZip) return "bad";
      if (encrypted) return "bad";
      return "ok";
    }
    if (ext === ".txt" || ext === ".mov") return "ok";
    return "bad";
  } catch {
    return "bad";
  }
}

function WarningTriangle({ size = 22 }) {
  return (
    <AlertTriangleOrange
      aria-hidden
      style={{ width: size, height: size, display: "block", flexShrink: 0 }}
    />
  );
}

function ErrorCircleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="9" fill="#F04438" />
      <path d="M6.2 6.2l5.6 5.6M11.8 6.2l-5.6 5.6" stroke="#FFFFFF" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SuccessCircleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="9" fill="#12B76A" />
      <path d="M5 9.1l2.6 2.6L13 6.4" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ExtractionFailedPanel({ onTryAgain, onGoBack }) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 4,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: "#FEF0C7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        <WarningTriangle size={28} />
      </Box>
      <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 800, fontSize: 18, color: "#1A1A1A", mt: 1.5 }}>
        Extraction Failed
      </Typography>
      <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#707070", mt: 1, maxWidth: 340, lineHeight: "20px" }}>
        ValidAIte encountered errors while processing your documents. Please check the files and try again.
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, mt: 2.5 }}>
        <Button disableElevation disableRipple onClick={onTryAgain} sx={{ ...footerPrimaryBtnSx, px: "20px" }}>
          Try Again
        </Button>
        <Button disableElevation disableRipple onClick={onGoBack} sx={footerGhostBtnSx}>
          Go Back
        </Button>
      </Box>
    </Box>
  );
}

function UploadFailedToast({ open, message, onClose, severity = "error" }) {
  useEffect(() => {
    if (!open || !message) return undefined;
    const timer = setTimeout(() => onClose?.(), 8000);
    return () => clearTimeout(timer);
  }, [open, message]);

  if (!open || !message) return null;
  const isWarning = severity === "warning";
  return (
    <Box
      role="alert"
      sx={{
        position: "fixed",
        bottom: 24,
        left: { xs: 24, md: 104 },
        zIndex: 10000,
        bgcolor: "#000000",
        color: "#FFFFFF",
        borderRadius: "12px",
        px: "16px",
        py: "14px",
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.32)",
        width: isWarning ? 420 : 400,
        maxWidth: "calc(100vw - 48px)",
        fontFamily: "Lato, sans-serif",
      }}
    >
      {isWarning ? (
        <Box sx={{ flexShrink: 0, mt: "2px", display: "flex" }}>
          <WarningTriangle size={24} />
        </Box>
      ) : (
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: "2px solid #F04438",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: "2px",
          }}
        >
          <Box component="span" sx={{ color: "#F04438", fontSize: 14, fontWeight: 800, lineHeight: 1 }}>
            !
          </Box>
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
        <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 14, fontWeight: 400, color: "#FFFFFF", lineHeight: "20px" }}>
          Upload failed
        </Typography>
        <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 14, fontWeight: 400, color: "#FFFFFF", lineHeight: "20px" }}>
          {message}
        </Typography>
      </Box>
      <Box
        component="button"
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        sx={{
          border: "none",
          bgcolor: "transparent",
          color: "#FFFFFF",
          p: 0,
          m: 0,
          cursor: "pointer",
          display: "flex",
          lineHeight: 1,
          "&:hover": { opacity: 0.8 },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </Box>
    </Box>
  );
}

export default function UploadTestCasesPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [pending, setPending] = useState([]);
  const [queue, setQueue] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [errorSeverity, setErrorSeverity] = useState("error");
  const [errorTick, setErrorTick] = useState(0);
  const [toast, setToast] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState(0);
  const [extraction, setExtraction] = useState(null);
  const [extractionFailed, setExtractionFailed] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [queueCases, setQueueCases] = useState([]);
  const [editingCase, setEditingCase] = useState(null);
  const [importBlocked, setImportBlocked] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);

  useEffect(() => {
    if (!queueCases.some((c) => c.status === "needs")) setImportBlocked(false);
  }, [queueCases]);

  const goBack = () => navigate(`/test-cases/${projectId}`);

  const showUploadError = (message, severity = "error") => {
    setErrorSeverity(severity);
    setError(message);
    setErrorTick((n) => n + 1);
  };

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const next = [];
    const existingKeys = new Set([...pending, ...queue].map((item) => fileKey(item.file)));
    let sizeRejectedName = "";
    let typeRejectedName = "";
    let processFailedCount = 0;
    let processTotal = 0;
    for (const file of incoming) {
      const key = fileKey(file);
      if (existingKeys.has(key)) continue;
      existingKeys.add(key);
      if (file.size > MAX_BYTES) {
        sizeRejectedName = file.name;
        continue;
      }
      if (!isAcceptedFile(file)) {
        typeRejectedName = file.name;
        next.push({ file, addedAt: Date.now(), rejected: true, processFailed: false });
        continue;
      }
      processTotal += 1;
      const processFailed = (await inspectExtractableFile(file)) !== "ok";
      if (processFailed) processFailedCount += 1;
      next.push({ file, addedAt: Date.now(), rejected: false, processFailed });
    }
    if (next.length) setPending((prev) => [...prev, ...next]);
    if (typeRejectedName) {
      showUploadError(unsupportedFormatMessage(typeRejectedName), "error");
    } else if (sizeRejectedName) {
      showUploadError(sizeLimitMessage(sizeRejectedName), "error");
    } else if (processFailedCount > 0) {
      showUploadError(processFailMessage(processFailedCount, processTotal), "warning");
      if (processFailedCount === processTotal) {
        setExtraction(null);
        setReviewOpen(false);
        setExtractionFailed({ failed: processFailedCount, total: processTotal });
      }
    }
  };

  const openPreview = (file) => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview({ file, url: URL.createObjectURL(file) });
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const removePending = (index) => {
    setPending((prev) => prev.filter((_, i) => i !== index));
  };

  const removeQueued = (index) => {
    setQueue((prev) => prev.filter((_, i) => i !== index));
  };

  const addToQueue = () => {
    if (extracting) return;
    if (!canQueue && !extractionFailed) return;
    setExtraction(null);
    setExtractionFailed(null);
    setReviewOpen(false);
    setExtracting(true);
    setExtractProgress(12);
  };

  useEffect(() => {
    if (!extracting) return undefined;
    const id = setInterval(() => {
      setExtractProgress((p) => Math.min(100, p + 5));
    }, 90);
    return () => clearInterval(id);
  }, [extracting]);

  useEffect(() => {
    if (!extracting || extractProgress < 100) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      const batch = pending.filter((item) => !item.rejected);
      Promise.all(
        batch.map((item) => (item.processFailed ? Promise.resolve("bad") : inspectExtractableFile(item.file)))
      ).then((statuses) => {
        if (cancelled) return;
        const failedItems = batch.filter((_, i) => statuses[i] !== "ok");
        const failed = failedItems.length;
        const total = batch.length;
        setExtracting(false);
        setExtractProgress(0);
        if (!total) {
          setExtractionFailed(null);
          setExtraction(null);
          return;
        }
        if (failed > 0) {
          const failedKeys = new Set(failedItems.map((item) => fileKey(item.file)));
          setPending((prev) =>
            prev.map((item) => (failedKeys.has(fileKey(item.file)) ? { ...item, processFailed: true } : item))
          );
          setQueue([]);
          setExtraction(null);
          setExtractionFailed({ failed, total });
          showUploadError(processFailMessage(failed, total), "warning");
          return;
        }
        setExtractionFailed(null);
        setQueue(batch);
        setExtraction(buildExtractionSummary(batch));
      });
    }, 280);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [extracting, extractProgress, pending]);

  const canQueue = pending.some((item) => !item.rejected && !item.processFailed);

  const handleImport = () => {
    if (!extraction) return;
    setQueueCases(buildQueueCases());
    setImportBlocked(false);
    setReviewOpen(true);
  };

  const confirmImport = () => {
    if (!reviewOpen || !queueCases.length) return;
    if (queueCases.some((c) => c.status === "needs")) {
      setImportBlocked(true);
      return;
    }
    setImportBlocked(false);
    setToast(`${queueCases.filter((c) => c.status === "ready").length} test cases ready to import.`);
  };

  const sourceFiles = pending.filter((item) => !item.rejected);

  return (
    <SelectDropdownTheme>
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#FFFFFF", minWidth: 0 }}>
      <IntakeGateTabsBar
        activeGate={ACTIVE_GATE}
        unlockedUpTo={ACTIVE_GATE}
        onGateClick={(gateIndex) => {
          if (gateIndex === 0) navigate(`/chat/${projectId}`);
          if (gateIndex === 1) navigate(`/gate1/${projectId}`);
        }}
        onBack={goBack}
        showViewSummary={false}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          pl: GATE_SUBTABS_PL,
          pr: GATE_SUBTABS_PR,
          height: 50,
          minHeight: 50,
          bgcolor: "#fff",
          borderBottom: "1px solid #E6E6E6",
          flexShrink: 0,
          overflowX: "auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "stretch", gap: GATE_SUBTABS_GAP, minWidth: "min-content" }}>
          {DOC_TABS.map((t, i) => {
            const active = i === 1;
            const Icon = t.Icon;
            return (
              <Box
                key={t.label}
                onClick={() => {
                  if (t.label === "Test Strategies") navigate(`/test-strategy/${projectId}`);
                  if (t.label === "Test Cases") goBack();
                }}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  height: "100%",
                  py: "8px",
                  boxSizing: "border-box",
                  cursor: "pointer",
                  color: active ? TAB_ACTIVE : TAB_INACTIVE,
                  "& svg": { width: 17, height: 17, display: "block", flexShrink: 0 },
                  "& svg path": { stroke: "currentColor" },
                  ...(active && {
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: "-8px",
                      right: "-8px",
                      bottom: 0,
                      height: "3px",
                      bgcolor: TAB_UNDERLINE,
                      pointerEvents: "none",
                    },
                  }),
                }}
              >
                <Icon aria-hidden />
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontSize: 14,
                    fontWeight: active ? 700 : 400,
                    lineHeight: "18px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          px: { xs: 2.5, md: 3 },
          pt: { xs: 2, md: 2.5 },
          pb: 1.5,
          bgcolor: "#FFFFFF",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={goBack}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            border: 0,
            bgcolor: "transparent",
            p: 0,
            mb: "4px",
            cursor: "pointer",
            color: "#1A1A1A",
            alignSelf: "flex-start",
          }}
        >
          <Box component="span" sx={{ fontSize: 22, lineHeight: "28px", fontWeight: 400 }}>
            ←
          </Box>
          <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 22, fontWeight: 800, lineHeight: "28px" }}>
            Upload test cases
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 14, color: "#707070", mb: 2.5, pl: "28px" }}>
          Upload one or more test cases — ValidAIte evaluates each before generating test cases
        </Typography>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: reviewOpen || extraction ? "minmax(260px, 0.72fr) minmax(480px, 1.28fr)" : "1fr 1fr",
            },
            gap: "16px",
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              border: "1px solid #E6E6E6",
              borderRadius: "8px",
              p: "16px",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {reviewOpen ? (
              <>
                <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 700, fontSize: 13, color: "#005BF0", mb: 1.5 }}>
                  Source Documents
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", overflow: "auto", minHeight: 0, pb: 1.5, borderBottom: "1px solid #E6E6E6" }}>
                  {sourceFiles.map((item, i) => (
                    <Box key={`${fileKey(item.file)}-src-${i}`} sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                      <FileDocIcon />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, fontWeight: 700, color: "#1A1A1A", lineHeight: "18px" }}>
                          {item.file.name}
                        </Typography>
                        <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 12, color: "#98A2B3", lineHeight: "18px", mt: "2px" }}>
                          {formatSize(item.file.size)} • {relativeLabel(item.addedAt)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
                        <IconButton size="small" aria-label="View file" onClick={() => openPreview(item.file)} sx={{ p: "4px" }}>
                          <VisibilityOutlinedIcon sx={{ fontSize: 18, color: "#005BF0" }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Delete file"
                          onClick={() => setFileToDelete({ index: pending.indexOf(item), name: item.file.name })}
                          sx={{ p: "4px" }}
                        >
                          <DeleteIcon style={{ width: 16, height: 18, display: "block" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <>
            <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 700, fontSize: 14, color: "#1A1A1A", mb: 1.5 }}>
              Upload test cases
            </Typography>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              onClick={() => inputRef.current?.click()}
              sx={{
                height: 118,
                flexShrink: 0,
                border: "1.5px dashed",
                borderColor: dragOver ? "#005BF0" : "#D0D5DD",
                borderRadius: "8px",
                bgcolor: "#FFFFFF",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                cursor: "pointer",
                px: 2,
              }}
            >
              <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#707070", lineHeight: "18px" }}>
                Drag and drop files here
              </Typography>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <Box
                  component="img"
                  src={browseIconSrc}
                  alt=""
                  sx={{ width: 22, height: 22, display: "block", objectFit: "contain" }}
                />
                <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 700, fontSize: 14, color: "#005BF0", lineHeight: 1 }}>
                  Browse
                </Typography>
              </Box>
              <input
                ref={inputRef}
                type="file"
                hidden
                multiple
                accept="*"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </Box>
            <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 12, color: "#98A2B3", mt: "8px" }}>
              Supported formats: PDF, MOV, XLS, TXT&nbsp;&nbsp;|&nbsp;&nbsp;Maximum size: 50MB
            </Typography>

            {pending.length > 0 && (
              <Box sx={{ mt: 2, display: "flex", flexDirection: "column", minHeight: 0, overflow: "auto" }}>
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 700,
                    fontSize: 13,
                    color: "#005BF0",
                    mb: 1.25,
                  }}
                >
                  Recently Uploaded Files ({String(pending.length).padStart(2, "0")})
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {pending.map((item, i) => {
                    const failedLook = Boolean(item.rejected);
                    const nameColor = failedLook ? "#F04438" : "#1A1A1A";
                    const iconColor = failedLook ? "#F04438" : "#005BF0";
                    return (
                    <Box
                      key={`${fileKey(item.file)}-${i}`}
                      sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}
                    >
                      <Box sx={{ flexShrink: 0, display: "flex", mt: "1px" }}>
                        <FileDocIcon error={failedLook} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, fontWeight: 700, color: nameColor, lineHeight: "18px" }}>
                          {item.file.name}
                        </Typography>
                        <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 12, color: "#98A2B3", lineHeight: "18px", mt: "2px" }}>
                          {formatSize(item.file.size)} • {relativeLabel(item.addedAt)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "2px", flexShrink: 0 }}>
                        <IconButton size="small" aria-label="View file" onClick={() => openPreview(item.file)} sx={{ p: "4px" }}>
                          <VisibilityOutlinedIcon sx={{ fontSize: 18, color: iconColor }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Delete file"
                          onClick={() => setFileToDelete({ index: i, name: item.file.name })}
                          sx={{ p: "4px" }}
                        >
                          <DeleteIcon style={{ width: 16, height: 18, display: "block" }} />
                        </IconButton>
                      </Box>
                    </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box sx={{ mt: "auto", pt: 2 }}>
              {extracting ? (
                <Button disableElevation disabled sx={addingBtnSx}>
                  Adding...
                </Button>
              ) : extraction || extractionFailed ? null : (
                <Button disableElevation disabled={!canQueue} onClick={addToQueue} sx={queueBtnSx}>
                  Add test cases to Queue
                </Button>
              )}
            </Box>
              </>
            )}
          </Box>

          <Box
            sx={{
              border: "1px solid #E6E6E6",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {reviewOpen ? (
              <QueueReviewPanel
                cases={queueCases}
                importBlocked={importBlocked}
                onDismissImportBlocked={() => setImportBlocked(false)}
                onEdit={setEditingCase}
                onDelete={(idOrIds) => {
                  const ids = new Set(Array.isArray(idOrIds) ? idOrIds : [idOrIds]);
                  setQueueCases((prev) => prev.filter((c) => !ids.has(c.id)));
                }}
                onDismissIncomplete={() => {
                  setQueueCases((prev) => prev.filter((c) => c.status !== "needs"));
                  setImportBlocked(false);
                }}
                onImportReady={() => {
                  setQueueCases((prev) => prev.filter((c) => c.status !== "duplicates"));
                  setImportBlocked(false);
                }}
                onImportSelected={(ids) => {
                  const selectedCases = queueCases.filter((c) => ids.includes(c.id));
                  if (selectedCases.some((c) => c.status === "needs")) {
                    setImportBlocked(true);
                    return;
                  }
                  setQueueCases((prev) => prev.filter((c) => c.status !== "duplicates"));
                  setImportBlocked(false);
                }}
              />
            ) : extracting ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 4,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 800, fontSize: 16, color: "#1A1A1A" }}>
                  Extracting test cases...
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontSize: 13,
                    color: "#707070",
                    mt: 1,
                    maxWidth: 280,
                    lineHeight: "20px",
                  }}
                >
                  ValidAIte is analyzing your documents and extracting test cases. This may take a few moments.
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={extractProgress}
                  sx={{
                    mt: 2.5,
                    width: 280,
                    maxWidth: "100%",
                    height: 6,
                    borderRadius: 999,
                    bgcolor: "#E8EEF6",
                    "& .MuiLinearProgress-bar": { bgcolor: "#005BF0", borderRadius: 999 },
                  }}
                />
              </Box>
            ) : extractionFailed ? (
              <ExtractionFailedPanel onTryAgain={addToQueue} onGoBack={goBack} />
            ) : extraction ? (
              <ExtractionCompletePanel summary={extraction} onReview={handleImport} />
            ) : queue.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 4,
                  textAlign: "center",
                }}
              >
                <QueueEmptyIllustration />
                <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 800, fontSize: 16, color: "#1A1A1A", mt: 1.5 }}>
                  Queue is empty
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontSize: 13,
                    color: "#707070",
                    mt: 1,
                    maxWidth: 300,
                    lineHeight: "20px",
                  }}
                >
                  Use the form on the left to add user stories. ValidAIte will autonomy-evaluate each one before generating test cases.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, overflow: "auto", p: 2 }}>
                <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 700, fontSize: 14, color: "#1A1A1A" }}>
                  Queue ({queue.length})
                </Typography>
                {queue.map((item, i) => (
                  <Box
                    key={`${fileKey(item.file)}-q-${i}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1.25,
                      py: 1,
                      border: "1px solid #E6E6E6",
                      borderRadius: "4px",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: "#344054" }}>
                        {item.file.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#98A2B3" }}>{formatSize(item.file.size)}</Typography>
                    </Box>
                    <IconButton size="small" aria-label="Remove from queue" onClick={() => removeQueued(i)}>
                      <CloseIcon sx={{ fontSize: 16, color: "#667085" }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          height: 56,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2.5, md: 3 },
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #F2F4F7",
          boxShadow: "0px -1px 8px 0px #2122260A",
        }}
      >
        <Button disableElevation disableRipple onClick={goBack} sx={footerGhostBtnSx}>
          Back
        </Button>
        <Button disableElevation disableRipple disabled={!reviewOpen} onClick={confirmImport} sx={footerPrimaryBtnSx}>
          Import Test Cases
        </Button>
      </Box>

      <ViewTestCaseDrawer
        open={Boolean(editingCase)}
        testCase={editingCase}
        onClose={() => setEditingCase(null)}
        onSave={(updated) => {
          setQueueCases((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
          setEditingCase(null);
          setToast("Test case saved.");
        }}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast("")} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert severity="success" onClose={() => setToast("")}>{toast}</Alert>
      </Snackbar>
      <UploadFailedToast
        key={errorTick}
        open={Boolean(error)}
        message={error}
        severity={errorSeverity}
        onClose={() => setError("")}
      />

      <DeleteUploadedDocumentDialog
        open={Boolean(fileToDelete)}
        fileName={fileToDelete?.name}
        onClose={() => setFileToDelete(null)}
        onConfirm={() => {
          if (fileToDelete && fileToDelete.index >= 0) removePending(fileToDelete.index);
          setFileToDelete(null);
        }}
      />
      <Dialog open={Boolean(preview)} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1, fontFamily: "Lato, sans-serif", fontSize: 16, fontWeight: 700 }}>
          {preview?.file?.name}
          <IconButton aria-label="Close preview" onClick={closePreview}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 240 }}>
          {preview && /pdf$/i.test(preview.file.type || preview.file.name) && (
            <Box component="iframe" title={preview.file.name} src={preview.url} sx={{ width: "100%", height: 480, border: 0 }} />
          )}
          {preview && /^text\//i.test(preview.file.type || "") && (
            <Box component="iframe" title={preview.file.name} src={preview.url} sx={{ width: "100%", height: 360, border: "1px solid #E6E6E6" }} />
          )}
          {preview && /^video\//i.test(preview.file.type || "") && (
            <Box component="video" src={preview.url} controls sx={{ width: "100%", maxHeight: 480 }} />
          )}
          {preview &&
            !/pdf$/i.test(preview.file.type || preview.file.name) &&
            !/^text\//i.test(preview.file.type || "") &&
            !/^video\//i.test(preview.file.type || "") && (
              <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 14, color: "#667085" }}>
                Preview isn’t available for this file type.{" "}
                <Box component="a" href={preview.url} download={preview.file.name} sx={{ color: "#005BF0", fontWeight: 700 }}>
                  Download
                </Box>
              </Typography>
            )}
        </DialogContent>
      </Dialog>
    </Box>
    </SelectDropdownTheme>
  );
}
